import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import api from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Plus, UserCheck, Search, X } from 'lucide-react'
import { HelpBanner } from '@/components/ui/help-banner'
import { useNavigate } from 'react-router-dom'
import type { PaginatedResponse, UserResponse, TenantResponse, GroupResponse } from '@/api/types'

function countryFlag(code: string): string {
  return [...code.toUpperCase()].map(c => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)).join('')
}

export function UsersPage() {
  const { t } = useTranslation(['users', 'common'])
  const navigate = useNavigate()
  const { user, impersonate, isImpersonating } = useAuth()
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [impersonateTarget, setImpersonateTarget] = useState<UserResponse | null>(null)
  const [impersonateLoading, setImpersonateLoading] = useState(false)
  const [impersonateError, setImpersonateError] = useState('')

  // Filters
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [tenantFilter, setTenantFilter] = useState('')
  const [groupFilter, setGroupFilter] = useState('')

  const queryClient = useQueryClient()
  const limit = 20
  const isPlatformAdmin = user?.platform_role === 'PLATFORM_ADMIN'

  const queryParams = useMemo(() => {
    const params = new URLSearchParams({ offset: String(offset), limit: String(limit) })
    if (search) params.set('q', search)
    if (statusFilter) params.set('status', statusFilter)
    if (tenantFilter) params.set('tenant_id', tenantFilter)
    if (groupFilter) params.set('group_id', groupFilter)
    return params.toString()
  }, [offset, limit, search, statusFilter, tenantFilter, groupFilter])

  const { data, isLoading } = useQuery({
    queryKey: ['users', queryParams],
    queryFn: () => api.get<PaginatedResponse<UserResponse>>(`/users?${queryParams}`),
  })

  // Fetch tenants for filter dropdown
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants-all'],
    queryFn: () => api.get<PaginatedResponse<TenantResponse>>('/tenants?offset=0&limit=100'),
  })
  const tenants = tenantsData?.data.data ?? []

  const { data: groupsData } = useQuery({
    queryKey: ['groups-all'],
    queryFn: () => api.get<GroupResponse[]>('/groups'),
  })
  const groups = groupsData?.data ?? []

  const createMutation = useMutation({
    mutationFn: () => api.post('/users', { email: newEmail, password: newPassword }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setDialogOpen(false)
      setNewEmail('')
      setNewPassword('')
    },
  })

  const users = data?.data.data ?? []
  const total = data?.data.total ?? 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold">{t('nav.users', { ns: 'common' })}</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger render={<Button />}>
            <Plus className="h-4 w-4 mr-2" />{t('newUser')}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('createUser')}</DialogTitle></DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate() }} className="flex flex-col gap-4">
              <div>
                <Label htmlFor="user-email">{t('labels.email', { ns: 'common' })}</Label>
                <Input id="user-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="user-password">{t('labels.password', { ns: 'common' })}</Label>
                <Input id="user-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
              </div>
              <Button type="submit" disabled={createMutation.isPending}>{t('buttons.create', { ns: 'common' })}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <HelpBanner storageKey="users">
        <p>{t('help.line1')}</p>
        <p>{t('help.line2')}</p>
      </HelpBanner>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <form
          className="relative flex-1 max-w-sm"
          onSubmit={(e) => { e.preventDefault(); setSearch(searchInput); setOffset(0) }}
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10 h-9"
          />
        </form>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setOffset(0) }}
          className="h-9 rounded-md border bg-background px-3 text-sm"
        >
          <option value="">{t('filters.allStatuses', { ns: 'common' })}</option>
          <option value="active">{t('statuses.active', { ns: 'common' })}</option>
          <option value="suspended">{t('statuses.suspended', { ns: 'common' })}</option>
          <option value="inactive">{t('statuses.inactive', { ns: 'common' })}</option>
        </select>
        <select
          value={tenantFilter}
          onChange={(e) => { setTenantFilter(e.target.value); setOffset(0) }}
          className="h-9 rounded-md border bg-background px-3 text-sm"
        >
          <option value="">{t('filters.allTenants', { ns: 'common' })}</option>
          {tenants.map((tn) => (
            <option key={tn.id} value={tn.id}>{tn.name}</option>
          ))}
        </select>
        <select
          value={groupFilter}
          onChange={(e) => { setGroupFilter(e.target.value); setOffset(0) }}
          className="h-9 rounded-md border bg-background px-3 text-sm"
        >
          <option value="">{t('filters.allGroups', { ns: 'common' })}</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
        {(search || statusFilter || tenantFilter || groupFilter) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setSearch(''); setSearchInput(''); setStatusFilter(''); setTenantFilter(''); setGroupFilter(''); setOffset(0) }}
          >
            <X className="h-4 w-4 mr-1" />{t('buttons.clear', { ns: 'common' })}
          </Button>
        )}
      </div>

      {isLoading ? <p className="text-muted-foreground">{t('loading', { ns: 'common' })}</p> : (
        <>
          <div className="border rounded-md">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium w-10"></th>
                <th className="text-left p-3 font-medium">{t('labels.username', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.email', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.group', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.status', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('lastLogin')}</th>
                <th className="text-left p-3 font-medium">{t('lastIP')}</th>
                <th className="text-left p-3 font-medium">{t('labels.created', { ns: 'common' })}</th>
                {isPlatformAdmin && <th className="text-right p-3 font-medium">{t('labels.actions', { ns: 'common' })}</th>}
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/users/${u.id}`)}>
                    <td className="p-3 text-center text-lg">{u.country_code ? countryFlag(u.country_code) : ''}</td>
                    <td className="p-3 font-medium">{u.username ?? '—'}</td>
                    <td className="p-3">{u.email}</td>
                    <td className="p-3">
                      {u.groups && u.groups.length > 0
                        ? u.groups.map(g => <Badge key={g.id} variant="outline" className="mr-1 text-xs">{g.name}</Badge>)
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="p-3"><Badge variant={u.status === 'active' ? 'default' : 'secondary'}>{u.status}</Badge></td>
                    <td className="p-3 text-muted-foreground">{u.last_login ? new Date(u.last_login).toLocaleString() : '—'}</td>
                    <td className="p-3 text-muted-foreground font-mono text-xs">{u.last_ip ?? '—'}</td>
                    <td className="p-3 text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                    {isPlatformAdmin && (
                      <td className="p-3 text-right">
                        {u.id !== user?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={isImpersonating}
                            title={t('impersonateUser')}
                            onClick={(e) => { e.stopPropagation(); setImpersonateTarget(u); setImpersonateError('') }}
                          >
                            <UserCheck className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={isPlatformAdmin ? 9 : 8} className="p-3 text-center text-muted-foreground">{t('empty')}</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-muted-foreground">{t('pagination.total', { ns: 'common' })} {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>{t('pagination.previous', { ns: 'common' })}</Button>
              <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>{t('pagination.next', { ns: 'common' })}</Button>
            </div>
          </div>
        </>
      )}

      {/* Impersonate confirmation dialog */}
      <Dialog open={!!impersonateTarget} onOpenChange={(open) => { if (!open) setImpersonateTarget(null) }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('impersonateUser')}</DialogTitle></DialogHeader>
          {impersonateError && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">{impersonateError}</div>
          )}
          <p className="text-sm text-muted-foreground">
            {t('impersonateConfirm', { email: impersonateTarget?.email, id: impersonateTarget?.id })}
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setImpersonateTarget(null)}>{t('buttons.cancel', { ns: 'common' })}</Button>
            <Button
              disabled={impersonateLoading}
              onClick={async () => {
                if (!impersonateTarget) return
                setImpersonateLoading(true)
                setImpersonateError('')
                try {
                  await impersonate(impersonateTarget.id)
                  setImpersonateTarget(null)
                } catch {
                  setImpersonateError(t('impersonateError'))
                } finally {
                  setImpersonateLoading(false)
                }
              }}
            >
              {impersonateLoading ? t('impersonateStarting') : t('confirmImpersonation')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
